import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { Permission, UserRole } from '@vns/core';
import { supabase } from './supabase';

export interface TeamMember {
  id: string;
  fullName: string;
  email: string;
  phone: string | null;
  designation: string | null;
  role: UserRole;
  isActive: boolean;
  projects: Array<{ id: string; name: string }>;
  permissions: Permission[];
}

export function useTeam() {
  return useQuery({
    queryKey: ['team'],
    queryFn: async (): Promise<TeamMember[]> => {
      const [people, access, perms] = await Promise.all([
        supabase.from('profiles')
          .select('id, full_name, email, phone, designation, role, is_active')
          .order('role').order('full_name'),
        supabase.from('staff_project_access').select('profile_id, projects(id, name)'),
        supabase.from('staff_permissions').select('profile_id, permission'),
      ]);
      if (people.error) throw new Error(people.error.message);
      if (access.error) throw new Error(access.error.message);
      if (perms.error) throw new Error(perms.error.message);

      const byProject = new Map<string, Array<{ id: string; name: string }>>();
      for (const a of access.data ?? []) {
        const p = a.projects as unknown as { id: string; name: string } | null;
        if (!p) continue;
        byProject.set(a.profile_id, [...(byProject.get(a.profile_id) ?? []), p]);
      }
      const byPerm = new Map<string, Permission[]>();
      for (const p of perms.data ?? []) {
        byPerm.set(p.profile_id, [...(byPerm.get(p.profile_id) ?? []), p.permission as Permission]);
      }

      return (people.data ?? []).map((u) => ({
        id: u.id,
        fullName: u.full_name,
        email: u.email,
        phone: u.phone,
        designation: u.designation,
        role: u.role as UserRole,
        isActive: u.is_active,
        projects: byProject.get(u.id) ?? [],
        permissions: byPerm.get(u.id) ?? [],
      }));
    },
  });
}

export interface NewStaff {
  full_name: string;
  email: string;
  password: string;
  phone?: string;
  designation?: string;
  role: UserRole;
  project_ids: string[];
  permissions: Permission[];
}

/**
 * Account creation goes through an Edge Function, not straight to the database.
 * Creating a login for somebody else needs the service-role key, which bypasses
 * every security policy and therefore cannot exist in browser code.
 */
export function useCreateStaff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: NewStaff) => {
      const { data, error } = await supabase.functions.invoke('create-staff', { body: input });
      if (error) {
        // The function returns a readable message in the body; surface that
        // rather than the generic "Edge Function returned a non-2xx status".
        let message = error.message;
        const res = (error as { context?: Response }).context;
        if (res && typeof res.json === 'function') {
          try {
            const parsed = await res.json();
            if (parsed?.error) message = parsed.error;
          } catch { /* keep the original message */ }
        }
        throw new Error(message);
      }
      return data as { id: string; email: string };
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['team'] }),
  });
}

/** Turning someone off keeps their history but stops them signing in. */
export function useSetStaffActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const { error } = await supabase.from('profiles').update({ is_active: isActive }).eq('id', id);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['team'] }),
  });
}

/** Replaces the whole grant set for one person — simpler than diffing. */
export function useUpdateAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ profileId, companyId, projectIds, permissions }: {
      profileId: string; companyId: string;
      projectIds: string[]; permissions: Permission[];
    }) => {
      const del = await Promise.all([
        supabase.from('staff_project_access').delete().eq('profile_id', profileId),
        supabase.from('staff_permissions').delete().eq('profile_id', profileId),
      ]);
      for (const d of del) if (d.error) throw new Error(d.error.message);

      if (projectIds.length) {
        const { error } = await supabase.from('staff_project_access').insert(
          projectIds.map((project_id) => ({ profile_id: profileId, project_id, company_id: companyId })),
        );
        if (error) throw new Error(error.message);
      }
      if (permissions.length) {
        const { error } = await supabase.from('staff_permissions').insert(
          permissions.map((permission) => ({ profile_id: profileId, permission, company_id: companyId })),
        );
        if (error) throw new Error(error.message);
      }
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['team'] }),
  });
}
